// Fetches the live vol-surface registry from Immersive and exposes the
// parsed list of available token-pairs. Replaces the hardcoded
// ['BTC', 'ETH'] list — new pairs appear in the pricer automatically as
// the engine adds them to its registry (no frontend change needed).
//
// Single fetch per WS connection (or env switch). Result is cached in
// module scope so multiple components reading the same data don't fire
// duplicate requests.
import { useContext, useEffect, useState } from 'react';

import { WebSocketContext } from '../websocket-context/websocket-provider';

export interface AvailableToken {
  source: string; // 'deribit' | 'binance' | 'mexc'
  baseQuote: string; // 'BTC' | 'SOL_USDC' | 'TRUMP_USDT' — wire identifier
  baseCcy: string; // 'BTC' | 'SOL' | 'TRUMP'
  quoteCcy: string; // 'USD' | 'USDC' | 'USDT'
  exchangeSymbol: string; // 'BTC-PERPETUAL', 'SOL_USDC-PERPETUAL'
  tenors: string[];
}

// Mirrors the comprehensive test's parse_token_pair helper. Used when
// pricing — engine expects und_token + acc_token, not a joined string.
export const parseTokenPair = (baseQuote: string): { base: string; quote: string } => {
  if (baseQuote.includes('_')) {
    const [base, quote] = baseQuote.split('_', 2);
    return { base, quote };
  }
  return { base: baseQuote, quote: 'USD' };
};

// Module-scope cache. One fetch per session; if the page is reopened we
// rely on React's effect re-running to refresh.
let cached: AvailableToken[] | null = null;
let inflight: Promise<AvailableToken[]> | null = null;

const TOKENS_REQUEST_ID = `AVAILABLE_TOKENS_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const requestTokens = (
  sendMessage: (msg: string) => void,
  subscribeToChannel: (id: string, handler: (resp: unknown) => void) => void,
  unsubscribeFromChannel?: (id: string) => void,
): Promise<AvailableToken[]> => {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = new Promise<AvailableToken[]>((resolve, reject) => {
    const reqId = `${TOKENS_REQUEST_ID}_${Date.now()}`;
    let settled = false;
    const finish = (out: AvailableToken[] | Error) => {
      if (settled) return;
      settled = true;
      if (unsubscribeFromChannel) {
        try {
          unsubscribeFromChannel(reqId);
        } catch {
          /* ignore */
        }
      }
      if (out instanceof Error) reject(out);
      else {
        cached = out;
        resolve(out);
      }
      inflight = null;
    };

    subscribeToChannel(reqId, (raw: unknown) => {
      const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
      if (resp?.error) {
        finish(new Error(`list_vol_surface_tokens error: ${JSON.stringify(resp.error)}`));
        return;
      }
      const result = resp?.result;
      const sources =
        result && typeof result === 'object'
          ? (result as Record<string, unknown>).sources
          : undefined;
      if (!Array.isArray(sources)) {
        finish(new Error('list_vol_surface_tokens: unexpected response shape'));
        return;
      }
      const out: AvailableToken[] = [];
      for (const entry of sources) {
        if (!entry || typeof entry !== 'object') continue;
        for (const [src, items] of Object.entries(entry as Record<string, unknown[]>)) {
          for (const itRaw of items ?? []) {
            const it =
              itRaw && typeof itRaw === 'object' ? (itRaw as Record<string, unknown>) : undefined;
            const bq = it?.base_quote;
            if (typeof bq !== 'string') continue;
            out.push({
              source: src,
              baseQuote: bq,
              baseCcy: typeof it?.base_ccy === 'string' ? it.base_ccy : bq,
              quoteCcy: typeof it?.quote_ccy === 'string' ? it.quote_ccy : 'USD',
              exchangeSymbol:
                typeof it?.exchange_symbol === 'string' ? it.exchange_symbol : `${bq}-PERPETUAL`,
              tenors: Array.isArray(it?.tenors)
                ? (it.tenors as unknown[]).filter((t): t is string => typeof t === 'string')
                : [],
            });
          }
        }
      }
      // De-duplicate by (source, base_quote). The registry can return the same
      // instrument more than once (observed: AVAX_USDC twice under deribit, the
      // two copies disagreeing on tenor coverage). Keep one entry per key,
      // preferring the one with the MOST tenors (so the fuller copy wins when, as
      // observed, one is a subset of the other). First-occurrence order is
      // preserved (Map keeps a key's original position).
      const deduped = new Map<string, AvailableToken>();
      for (const t of out) {
        const key = `${t.source}:${t.baseQuote}`;
        const prev = deduped.get(key);
        if (!prev || t.tenors.length > prev.tenors.length) deduped.set(key, t);
      }
      finish([...deduped.values()]);
    });

    sendMessage(
      JSON.stringify({
        jsonrpc: '2.0',
        id: reqId,
        method: 'list_vol_surface_tokens',
        params: { source: [] },
      }),
    );

    // Safety: timeout in 15s so the UI isn't stuck on "loading…" forever
    // if the server never replies.
    setTimeout(() => {
      if (!settled) finish(new Error('list_vol_surface_tokens timed out'));
    }, 15000);
  });

  return inflight;
};

export const useAvailableTokens = (): {
  tokens: AvailableToken[];
  isLoading: boolean;
  error: string | null;
} => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const [tokens, setTokens] = useState<AvailableToken[]>(cached ?? []);
  const [isLoading, setIsLoading] = useState<boolean>(!cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cached) {
      setTokens(cached);
      setIsLoading(false);
      return;
    }
    if (!isConnected || !sendMessage || !subscribeToChannel) return;
    let cancelled = false;
    setIsLoading(true);
    requestTokens(sendMessage, subscribeToChannel, unsubscribeFromChannel)
      .then((list) => {
        if (cancelled) return;
        setTokens(list);
        setIsLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isConnected, sendMessage, subscribeToChannel, unsubscribeFromChannel]);

  return { tokens, isLoading, error };
};

// Convenience accessor for code paths that need synchronous access to a
// cached snapshot (e.g. derived selectors). Returns [] until the hook
// has populated the cache.
export const peekAvailableTokens = (): AvailableToken[] => cached ?? [];
